import { Progress } from "@mantine/core";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { MantineReactTable, useMantineReactTable } from "mantine-react-table";
import { type UIEvent, useCallback, useEffect, useRef } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { GroupChildrenRepository } from "shared/components/organization/groups/data";
import classes from "shared/components/organization/groups/groups.module.css";
import { groupTableDefinition } from "shared/components/organization/groups/GroupTableDefinition";
import { permissionCheck, usePermissions } from "shared/stores/oidc";
import { logger } from "shared/utils/logger";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";
import { Permission } from "src/gql/graphql";
import { container } from "tsyringe";

export const GroupFilterTable = (props: { selectedGroupFilterId: string }) => {
    const nav = useNavigate();
    const search = useSearch({ from: "/_auth/organization/groups" });

    const tableContainerRef = useRef<HTMLDivElement>(null);
    const containerClientHeight = tableContainerRef.current?.clientHeight;
    const containerScrollHeight = tableContainerRef.current?.scrollHeight;

    const { permissions } = usePermissions();

    // query to populate the table after filtering by a parent
    const groupChildrenRepo = container.resolve(GroupChildrenRepository);
    const groupChildrenLoader = groupChildrenRepo.get({ parentId: props.selectedGroupFilterId, queryMembership: false });
    const groupChildrenData = groupChildrenLoader.useItems();
    const { isLoading: groupChildrenIsLoading, hasNextPage: groupChildrenHasNextPage } = groupChildrenLoader.useStatus();

    // Get data of the row to pin when filtering by parent
    const { data: rowPinGroup, isLoading: rowPinIsLoading } = useSuspenseQuery({
        ...queries.rowPin(props.selectedGroupFilterId),
    });
    const rowPin = rowPinGroup?.group;
    const groupChildren = !!rowPin ? groupChildrenData.concat(rowPin) : groupChildrenData;

    const table = useMantineReactTable({
        columns: groupTableDefinition,
        data: groupChildren,
        meta: {
            canNavigateTo: (groupId: string) => permissionCheck(permissions, Permission.GroupUsersView, groupId),
        },
        enableColumnActions: false,
        enableBottomToolbar: false,
        enablePagination: false,
        enableSelectAll: false,
        enableRowVirtualization: true,
        enableRowPinning: true,
        rowPinningDisplayMode: "top",
        enableSorting: false,
        enableTopToolbar: false,
        getRowId: (row) => row.id,
        mantineTableContainerProps: {
            ref: tableContainerRef,
            style: { height: "70vh", maxHeight: "1900px", flexGrow: 1 },
            onScroll: (event: UIEvent<HTMLDivElement>) => fetchMoreChildren(event.target as HTMLDivElement),
        },
        mantineTableBodyRowProps: ({ row }) => ({
            onClick: (event) => {
                const target = event.target as HTMLElement;
                if (
                    // Checks if we're clicking a badge
                    !target.closest(".mantine-Badge-root") &&
                    // Checks if we're clicking one of the options in the "View All" popover
                    !target.closest(`.${classes.viewMoreDropdownItem}`) &&
                    // Checks if we're clicking the "Select" button (or any action icon)
                    !target.closest(".mantine-ActionIcon-root") &&
                    permissionCheck(permissions, Permission.GroupSubgroupManage, row.id)
                ) {
                    nav({ to: "/organization/groups/$groupId", params: { groupId: row.id }, search: { parent: search.parent } }).catch(
                        notify.catch,
                    );
                }
            },
            style: { cursor: permissionCheck(permissions, Permission.GroupSubgroupManage, row.id) ? "pointer" : undefined },
        }),
        initialState: {
            columnVisibility: { "mrt-row-pin": false },
        },
        state: {
            isLoading: groupChildrenIsLoading || rowPinIsLoading,
            rowPinning: {
                top: !rowPinIsLoading ? [props.selectedGroupFilterId] : undefined,
            },
        },
        rowVirtualizerOptions: {
            overscan: 5,
        },
    });

    const fetchMoreChildren = useCallback(
        (containerRefElement?: HTMLDivElement | null) => {
            if (containerRefElement) {
                const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
                //once the user has scrolled within 400px of the bottom of the table, fetch more data if we can
                if (scrollHeight - scrollTop - clientHeight < 200 && !groupChildrenIsLoading && groupChildrenHasNextPage) {
                    groupChildrenLoader.loadMore().catch(logger.error);
                }
            }
        },
        [groupChildrenIsLoading, groupChildrenHasNextPage],
    );

    useEffect(() => {
        if (
            !!containerScrollHeight &&
            !!containerClientHeight &&
            containerScrollHeight <= containerClientHeight &&
            groupChildrenHasNextPage &&
            !groupChildrenIsLoading
        )
            groupChildrenLoader.loadMore().catch(logger.error);
    }, [containerScrollHeight, containerClientHeight, groupChildrenHasNextPage, groupChildrenIsLoading]);

    return (
        <>
            <MantineReactTable table={table} />
            {groupChildrenIsLoading && <Progress value={100} striped animated />}
        </>
    );
};

const queries = {
    rowPin: (id: string) =>
        queryOptions({
            queryKey: ["group", "id", id, "rowPin"],
            queryFn: () => {
                if (!id) return null;
                return httpPostGraphql(
                    graphql(`
                        query RowPin($id: UUID!) {
                            group(id: $id) {
                                aboutMembership {
                                    allMembersCount
                                }
                                ...FullGroupLike
                            }
                        }
                    `),
                    { id },
                );
            },
        }),
};
